'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

import {
	canonicalizeWindowLocationHash,
	normalizeChainedHashFragment
} from '@/_lib/next-link-href'

import { clearSavedScrollY } from './scroll-position-persistence'

function smoothScrollToHash(hash: string, options?: { offsetTop?: number }) {
	const el = document.getElementById(hash)
	if (!el) return false
	const headerHeight = (document.querySelector('header.header') as HTMLElement)?.offsetHeight ?? 0
	const offsetY = options?.offsetTop ?? headerHeight
	const scrollTop = el.getBoundingClientRect().top + window.scrollY - offsetY
	window.scrollTo({
		top: scrollTop,
		behavior: 'smooth'
	})
	return true
}

const HASH_SCROLL_RETRY_DELAYS_MS = [0, 100, 300, 600, 1200]

function scheduleScrollToHash(hash: string): () => void {
	const timeouts: ReturnType<typeof setTimeout>[] = []
	let index = 0
	const tryScroll = () => {
		if (smoothScrollToHash(hash)) return
		index += 1
		if (index < HASH_SCROLL_RETRY_DELAYS_MS.length) {
			const delay =
				HASH_SCROLL_RETRY_DELAYS_MS[index] - HASH_SCROLL_RETRY_DELAYS_MS[index - 1]
			timeouts.push(setTimeout(tryScroll, delay))
		}
	}
	tryScroll()
	return () => timeouts.forEach(t => clearTimeout(t))
}

/**
 * Global scroll behavior on client-side navigation and hash URLs.
 * - On pathname change: if URL has hash → scroll to element (100ms delay); else → scroll to top.
 * - On initial load with hash → scroll to element (replaces per-page ZQueryScrollToSection).
 * - Listens to hashchange for same-page hash navigation (e.g. in-page links).
 * For smooth scroll on hash link click, use handleProgramaticScroll from @/_lib/utils on the link.
 * Scroll restoration for refresh/back is handled by ScrollPositionPersistence (sessionStorage).
 */
export function ScrollToTopOnNavigate() {
	const pathname = usePathname()
	const prevPathname = useRef<string | null>(null)
	const fromHistoryTraversalRef = useRef(false)

	useEffect(() => {
		const onPopState = () => {
			fromHistoryTraversalRef.current = true
		}
		window.addEventListener('popstate', onPopState)
		return () => window.removeEventListener('popstate', onPopState)
	}, [])

	useEffect(() => {
		canonicalizeWindowLocationHash()
		const hash = normalizeChainedHashFragment(window.location.hash?.replace(/^#/, '') ?? '')
		if (hash) {
			const cleanup = scheduleScrollToHash(hash)
			prevPathname.current = pathname
			fromHistoryTraversalRef.current = false
			return cleanup
		}
		if (prevPathname.current !== null && !fromHistoryTraversalRef.current) {
			clearSavedScrollY(pathname)
			window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
		}
		fromHistoryTraversalRef.current = false
		prevPathname.current = pathname
	}, [pathname])

	useEffect(() => {
		const onHashChange = () => {
			canonicalizeWindowLocationHash()
			const hash = normalizeChainedHashFragment(window.location.hash?.replace(/^#/, '') ?? '')
			// Do not call scheduleScrollToHash(hash)() — cleanup clears retry timeouts mid-flight.
			if (hash) scheduleScrollToHash(hash)
		}
		window.addEventListener('hashchange', onHashChange)
		return () => window.removeEventListener('hashchange', onHashChange)
	}, [])

	return null
}
